<template>
  <div class="contact-card">
    <div class="card-header" :style="{ background: headerGradient }">
      <div class="header-icon">
        <span class="material-symbols-outlined">{{ service.icon }}</span>
      </div>
      <h3 class="header-title">{{ service.title }}</h3>
    </div>

    <div class="card-body">
      <div 
        v-for="(contact, index) in service.contacts"
        :key="index"
        class="contact-item"
        :class="{ 'priority': contact.priority }"
        @click="handleContactClick(contact)"
      >
        <div class="contact-icon">
          <span class="material-symbols-outlined">{{ contact.icon }}</span>
        </div>
        <div class="contact-info">
          <span class="contact-label">{{ contact.label }}</span>
          <span class="contact-value">{{ contact.value }}</span>
        </div>
        <div class="contact-action">
          <span class="material-symbols-outlined">
            {{ contact.type === 'link' ? 'open_in_new' : contact.type === 'email' ? 'mail' : 'call' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ContactCard',
  props: {
    service: {
      type: Object,
      required: true
    }
  },
  computed: {
    headerGradient() {
      const baseColor = this.service.color || '#0068A2';
      // Create a lighter version for gradient
      return `linear-gradient(135deg, ${baseColor} 0%, ${this.lightenColor(baseColor, 20)} 100%)`;
    }
  },
  methods: {
    handleContactClick(contact) {
      if (contact.link) {
        window.open(contact.link, contact.type === 'link' ? '_blank' : '_self');
      }
    },
    lightenColor(color, percent) {
      // Simple color lightening (works for hex colors)
      const num = parseInt(color.replace('#', ''), 16);
      const amt = Math.round(2.55 * percent);
      const R = (num >> 16) + amt;
      const G = (num >> 8 & 0x00FF) + amt;
      const B = (num & 0x0000FF) + amt;
      return '#' + (
        0x1000000 +
        (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
        (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
        (B < 255 ? B < 1 ? 0 : B : 255)
      ).toString(16).slice(1);
    }
  }
};
</script>

<style scoped>
.contact-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
}

.contact-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
}

/* Card Header */
.card-header {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  color: white;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.header-icon .material-symbols-outlined {
  font-size: 28px;
  color: white;
}

.header-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

/* Card Body */
.card-body {
  padding: 0.5rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  margin: 0.25rem;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
}

.contact-item:hover {
  background: #f8f9fa;
  transform: translateX(4px);
}

.contact-item.priority {
  background: #fff3cd;
  border: 2px solid #ffc107;
}

.contact-item.priority:hover {
  background: #ffe69c;
}

.contact-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.contact-item.priority .contact-icon {
  background: linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%);
}

.contact-icon .material-symbols-outlined {
  font-size: 22px;
  color: #1976d2;
}

.contact-item.priority .contact-icon .material-symbols-outlined {
  color: #f57c00;
}

.contact-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.contact-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.contact-value {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
  word-break: break-all;
}

.contact-action {
  flex-shrink: 0;
  color: #9ca3af;
  transition: all 0.2s ease;
}

.contact-item:hover .contact-action {
  color: #0068A2;
  transform: translateX(2px);
}

.contact-action .material-symbols-outlined {
  font-size: 20px;
}

/* Responsive */
@media (max-width: 640px) {
  .card-header {
    padding: 1.25rem;
  }

  .header-icon {
    width: 40px;
    height: 40px;
  }

  .header-icon .material-symbols-outlined {
    font-size: 24px;
  }

  .header-title {
    font-size: 1.125rem;
  }

  .contact-item {
    padding: 0.875rem;
  }

  .contact-value {
    font-size: 0.875rem;
  }
}
</style>
